/**
 * Date / time helpers for the Rides API conventions:
 *   - dates arrive as `yyyy-MM-dd` strings or `yyyyMMdd` numbers
 *   - stamps arrive as `yyyyMMddHHmmss` numbers (user-local unless noted)
 *   - times are `HH:mm` (sometimes `HH:mm:ss`)
 * Everything renders in plain language ("Sunday, 25 October 2026").
 */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

/** Parse `yyyy-MM-dd`, `yyyyMMdd` (number or string) or an ISO string into a local Date. */
export function parseApiDate(value: string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    const s = String(value);
    if (s.length === 8) return new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)));
    if (s.length === 14) return stampToDate(value);
    return null;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (/^\d{8}$/.test(value)) return parseApiDate(Number(value));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `yyyyMMddHHmmss` → local Date. */
export function stampToDate(stamp: number | string | null | undefined): Date | null {
  if (stamp === null || stamp === undefined || stamp === '' || stamp === 0) return null;
  const s = String(stamp).padStart(14, '0');
  if (s.length !== 14) return null;
  return new Date(
    Number(s.slice(0, 4)),
    Number(s.slice(4, 6)) - 1,
    Number(s.slice(6, 8)),
    Number(s.slice(8, 10)),
    Number(s.slice(10, 12)),
    Number(s.slice(12, 14)),
  );
}

export function toYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function todayYmd(): string {
  return toYmd(new Date());
}

/** "Sunday, 25 October 2026" */
export function formatDate(value: string | number | Date | null | undefined): string {
  const d = value instanceof Date ? value : parseApiDate(value);
  if (!d) return '';
  return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Sun 25 Oct" */
export function formatShortDate(value: string | number | Date | null | undefined): string {
  const d = value instanceof Date ? value : parseApiDate(value);
  if (!d) return '';
  return `${DAYS[d.getDay()].slice(0, 3)} ${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
}

/** "October 2026" — used for month group headings. */
export function monthLabel(value: string | number | Date | null | undefined): string {
  const d = value instanceof Date ? value : parseApiDate(value);
  if (!d) return '';
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** { d: "25", m: "Oct" } for the event date block. */
export function dateBlock(value: string | number | null | undefined): { d: string; m: string } {
  const d = parseApiDate(value);
  if (!d) return { d: '--', m: '' };
  return { d: pad2(d.getDate()), m: MONTHS[d.getMonth()].slice(0, 3) };
}

/** "HH:mm" (drops seconds; tolerates null). */
export function formatTime(value: string | null | undefined): string {
  if (!value) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!m) return value;
  return `${pad2(Number(m[1]))}:${m[2]}`;
}

/** "Sun 25 Oct, 08:12" for audit stamps. */
export function formatStamp(stamp: number | null | undefined): string {
  const d = stampToDate(stamp);
  if (!d) return '';
  return `${formatShortDate(d)}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Combine an API date + HH:mm into a local Date (time defaults to 00:00). */
export function combineDateTime(date: string | number | null | undefined, time?: string | null): Date | null {
  const d = parseApiDate(date);
  if (!d) return null;
  const t = formatTime(time);
  if (t) {
    const [h, m] = t.split(':').map(Number);
    d.setHours(h, m, 0, 0);
  }
  return d;
}

/**
 * Plain-language countdown: "in 3 days", "tomorrow", "today at 09:45", "in 2 hours", "now",
 * "2 days ago". `target` is the ride's departure (or event) moment.
 */
export function countdown(target: Date | null | undefined, now: Date = new Date()): string {
  if (!target) return '';
  const ms = target.getTime() - now.getTime();
  const abs = Math.abs(ms);
  const minutes = Math.round(abs / 60000);
  const hours = Math.round(abs / 3600000);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const dayDiff = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86400000);

  if (ms < 0) {
    if (minutes < 60) return minutes <= 1 ? 'now' : `${minutes} min ago`;
    if (dayDiff === 0) return `${plural(hours, 'hour')} ago`;
    if (dayDiff === -1) return 'yesterday';
    return `${-dayDiff} days ago`;
  }
  if (minutes < 60) return minutes <= 1 ? 'now' : `in ${minutes} min`;
  if (dayDiff === 0) return `today at ${pad2(target.getHours())}:${pad2(target.getMinutes())}`;
  if (dayDiff === 1) return 'tomorrow';
  if (dayDiff < 7) return `in ${dayDiff} days`;
  if (dayDiff < 30) return `in ${Math.round(dayDiff / 7)} week${Math.round(dayDiff / 7) === 1 ? '' : 's'}`;
  return formatShortDate(target);
}

/** True when the API date is before today. */
export function isPastDate(value: string | number | null | undefined): boolean {
  const d = parseApiDate(value);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

/** Initials for an avatar ("Grace Molefe" → "GM"). */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';
}

/** "1 seat" / "3 seats" */
export function plural(n: number, singular: string, pluralForm?: string): string {
  return `${n} ${n === 1 ? singular : pluralForm ?? `${singular}s`}`;
}
